import {Component, Element, State, getAssetPath, h, Prop, Event, type EventEmitter} from '@stencil/core';
import {widgetStatistics} from './product-selector-statistics';
import type {AnalyticsGenericEvent} from '../../../utils/analytics/AnalyticsGenericEvent';


@Component({
  tag: 'uiaas-search-widget-cta-product-selector',
  styleUrl: 'product-selector.scss',
  assetsDirs: ['assets/assets-cta-ps'],
  shadow: true
})
export class WidgetCtaProductSelector {

  @Prop() readonly country: string = 'us';
  @Prop() readonly language: string = 'en';
  @Prop() readonly image: string;
  @Prop() readonly link: string;
  @Prop() readonly appName: string;
  @Prop() readonly anchorTarget: string = '';
  @Prop() readonly isFullHeight: boolean = false;
  @Prop() readonly isDashboardView: boolean = false;
  @Prop() readonly isDashboardMobile: boolean = false;
  @Prop() readonly dashboardTabName: string;
  @Prop() readonly analyticsTabName?: string;

  @Element()
    el: HTMLElement;

  @State()
    tabName: string;

  @Event({
    eventName: 'search:widgetEvent',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
    statisticsEventEmitter: EventEmitter<AnalyticsGenericEvent>;

  private background() {
      if (this.image) {
        return this.image;
      }
      return getAssetPath('./assets/assets-cta-ps/back1.jpg');
  }


  private makeLabel() {
      const language = this.language.toLowerCase();

      switch (language) {
      default:
      case 'en':
        return ['Looking for a product?', 'Try our Product selector', 'Check it out'];
      case 'de':
        return ['Suchen Sie ein Produkt?', 'Nutzen Sie bitte unser Produkt-Auswahltool.', 'Probieren Sie es aus'];
      case 'pl':
        return ['Poszukujesz produktu?', 'Sprawdź nasz Selektor Produktów', 'Zajrzyj tutaj'];
      case 'fr':
        return ['Vous cherchez un produit?', 'Essayez notre Sélecteur de Produits', 'Testez-le'];
      case 'ru':
        return ['Ищете продукт?', 'Инструмент по выбору продуктов', 'Начать'];
      case 'nl':
        return ['Zoekt u een product?', 'Gebruik de productselector', 'Direct bekijken'];
      case 'it':
        return ['Cerchi un prodotto?', 'Avvia il selettore', 'Provalo ora!'];
      case 'sv':
        return ['Letar du efter en produkt?', 'Prova våra produktväljare', 'Testa nu'];
      case 'es':
        return ['\t¿Buscas un producto?', 'Pruebe nuestro Selector de productos', 'Echale un vistazo'];
      case 'ja':
        return ['製品をお探しですか？', '製品選定ツールをご活用ください。', '使ってみる'];
      case 'zh':
        return ['想尋找特定的產品嗎？', '試試我們的產品搜尋工具', '深入了解'];
      case 'fi':
        return ['Etsitkö tuotetta?', 'Kokeile tuotevalitsintamme', 'Kokeile nyt'];
      }
  }

  private makeLink() {
      if (this.link)
        return this.link;
      const country = this.country.toLowerCase();
      const language = this.language.toLowerCase();

      let productSelectorLink = `https://www.se.com/${country}/${language}/work/support/product-selector/`;

      productSelectorLink = this.appName == 'apc' ?
        `https://www.apc.com/${country}/${language}/support/resources-tools/product-selectors.jsp` :
        country == 'us' || country == 'ca' ?
          `https://www.se.com/${country}/${language}/work/support/resources-and-tools/calculators-and-online-tools/ez-selectors/` :
          country == 'cn' && language == 'zh' ?
            'https://www.schneider-electric.cn/zh/work/support/product-selector/' : productSelectorLink;

      return productSelectorLink;
  }

  private emitSearchStatisticsEvent = (e: AnalyticsGenericEvent) => {
      this.statisticsEventEmitter.emit(
        e
      );
  };

  connectedCallback() {
      const closestTabEl = this.el.closest('uiaas-search-tab');
      this.tabName = closestTabEl?.['tabName'] || this.dashboardTabName;
  }

  render() {
      const tab = this.analyticsTabName ?? this.tabName;
      const labels = this.makeLabel();
      const link = this.makeLink();
      return <a class={'link product-selector-link ' + (this.isFullHeight ? 'product-selector-link--full' : '')}
        href={link}
        target={this.anchorTarget}
        onMouseDown={(e) => this.emitSearchStatisticsEvent(widgetStatistics.createUserRedirectionStatisticsEvent(tab, e.button == 0 ? 'left' : 'right', link))}
        onKeyDown={e => { if (e.key === 'Enter') {
          this.emitSearchStatisticsEvent(widgetStatistics.createUserRedirectionStatisticsEvent(tab, e.key, link));
        }
        }}>
        {this.isDashboardMobile ? <div class='cta-product-selector-dashboard-mobile'>
          <div class='cta-product-selector-wrapper'>
            <se-icon class="icon-large support-icon-dashboard">gesture_selector</se-icon>
            <div>
              {labels && labels[0] ? <p class="subtitle1-dashboard">{labels[0]}</p> : ''}
              {labels && labels[1] ? <p class="subtitle2-dashboard">{labels[1]}</p> : ''}
            </div>
          </div>

          {labels && labels[2] ? <div class="support-link-dashboard">
            <qds-standalone-link >{labels[2]}</qds-standalone-link>
          </div> : ''}
        </div>
          : <div class={this.isDashboardView ? 'cta-product-selector-dashboard' : 'cta-product-selector'}>
            <div class={this.isDashboardView ? 'product-selector-header' : ''}>
              <se-icon class="icon-large support-icon">gesture_selector</se-icon>
              {labels && labels[0] ? <p class="subtitle1">{labels[0]}</p> : ''}
            </div>
            {labels && labels[1] ? <p class="subtitle2">{labels[1]}</p> : ''}
            <div class="link-spacer"></div>
            {labels && labels[2] ?
              <div class="support-button-container">
                <qds-standalone-link importance="emphasized" class="support-link">{labels[2]}</qds-standalone-link>
              </div> : ''}
          </div>}
      </a>;
  }

}
